import {
	Button,
	HStack,
	Select,
	VStack,
	Text,
} from '@montel/montelpro-shared-components-v2';
import {
	ACCOUNT_API_GET_ACCOUNT_LIST,
	ACCOUNT_API_GET_ACCOUNTS_BY_COMPANY,
	SUBSCRIPTIONS_ASSIGN_SEAT,
} from 'apiRoutes';
import { MontelAccount } from 'domain/account/interfaces';
import {
	Subscription,
	SubscriptionType,
} from 'domain/subscription/interfaces/Subscription';
import { useGet } from 'hooks/useGet';
import useSubmit, { POST } from 'hooks/useSubmit';
import { Fragment, useEffect, useState } from 'react';
import styled from 'styled-components';
import { RequestStatus } from 'types/RequestStatus';
import { AssignSeatToExternalUserModal } from '../AssignSeatToExternalUserModal/AssignSeatToExternalUserModal';
import { InviteUserModal } from '../InviteUserModal/InviteUserModal';
import useAuthRoles from 'hooks/useAuthRoles';
import { AdminRole } from 'domain/account/enums/AdminRole';

const ClickableText = styled(Text)`
	cursor: pointer;
	color: ${({ theme }) => theme.colors.iconBrand};
`;

const AssignSeatSection: React.FC<{
	subscription: Subscription;
	getSubscription: () => void;
}> = ({ subscription, getSubscription }) => {
	if (!subscription || !subscription.companyId) {
		return null;
	}

	const [selectedUser, setSelectedUser] = useState<string | null>(null);
	const [allUsers, setAllUsers] = useState<MontelAccount[]>([]);
	const [
		showAssignSeatToExternalUserModal,
		setShowAssignSeatToExternalUserModal,
	] = useState(false);
	const [showInviteUserModal, setShowInviteUserModal] = useState(false);

	const { adminIsRestrictedToRole: isRestrictedToRole } = useAuthRoles();

	useEffect(() => {
		GetCompanyUsers();
	}, [subscription]);

	const {
		data: companyUsers,
		status: companyUsersStatus,
		getData: GetCompanyUsers,
	} = useGet<MontelAccount[]>(
		`${ACCOUNT_API_GET_ACCOUNTS_BY_COMPANY}?companyId=${subscription.companyId}`,
	);

	const [assignSeat, assignSeatStatus] = useSubmit(
		`${SUBSCRIPTIONS_ASSIGN_SEAT}`,
		POST,
		{
			messageOnError: 'An error occurred while assigning the user.',
			messageOnSuccess: 'User assigned successfully.',
			showApiError: true,
		},
	);

	const { getData: getAllUsers, status: getAllUsersStatus } = useGet<
		MontelAccount[]
	>(`${ACCOUNT_API_GET_ACCOUNT_LIST}`);

	const onAssignSeat = async () => {
		if (selectedUser) {
			const result = await assignSeat({
				subscriptionId: subscription.id,
				subscriberId: selectedUser,
			});
			if (result.success) {
				setSelectedUser(null);
				getSubscription();
			}
		}
	};

	const onAssignSeatToExternalUserClicked = () => {
		setShowAssignSeatToExternalUserModal(true);
		if (allUsers.length) {
			return;
		}

		getAllUsers().then((users) => {
			if (users) {
				setAllUsers(users);
			}
		});
	};

	const usersWithSeat = subscription.subscribers?.map((s) => s.id) || [];
	const availableCompanyUsers = companyUsers?.filter(
		(user) => !usersWithSeat.includes(user.id),
	);
	const availableExternalUsers = allUsers.filter(
		(u) =>
			!companyUsers?.some((cu) => cu.id === u.id) &&
			!subscription.subscribers?.some((s) => s.id === u.id),
	);

	const hasAvailableSeats = subscription.occupiedSeats < subscription.seats;
	const canAssignSeats =
		!isRestrictedToRole(AdminRole.SALES) ||
		(isRestrictedToRole(AdminRole.SALES) &&
			(subscription.type == SubscriptionType.Paid ||
				subscription.type == SubscriptionType.Trial));

	return companyUsersStatus === RequestStatus.SUCCESS &&
		hasAvailableSeats &&
		canAssignSeats ? (
		<Fragment>
			<VStack gap="xxl">
				<HStack gap="md">
					<Select
						label="Assign Seat to company user"
						placeholder="Select a user"
						options={availableCompanyUsers?.map((user) => ({
							value: user.id,
							label: `${user.firstName} ${user.lastName} (${user.email.value})`,
						}))}
						onChange={(userOption) => {
							if (userOption) {
								setSelectedUser(userOption.value);
							}
						}}
						isDisabled={
							subscription.occupiedSeats >= subscription.seats
						}
					/>
					<Button
						text="Assign"
						size="sm"
						isDisabled={!selectedUser}
						isLoading={assignSeatStatus === RequestStatus.LOADING}
						loadingText="Assigning..."
						variant="primary"
						onClick={onAssignSeat}
					/>
				</HStack>
				<ClickableText
					onClick={onAssignSeatToExternalUserClicked}
					size="xs"
				>
					Assign seat to external user
				</ClickableText>
				<ClickableText
					onClick={() => setShowInviteUserModal(true)}
					size="xs"
				>
					Invite user
				</ClickableText>
			</VStack>
			<AssignSeatToExternalUserModal
				showModal={showAssignSeatToExternalUserModal}
				setShowModal={setShowAssignSeatToExternalUserModal}
				subscriptionId={subscription.id}
				onSeatAssigned={getSubscription}
				allUsers={availableExternalUsers}
				allUsersStatus={getAllUsersStatus}
			/>
			<InviteUserModal
				showModal={showInviteUserModal}
				setShowModal={setShowInviteUserModal}
				subscriptionId={subscription.id}
				onUserInvited={GetCompanyUsers}
				companyId={subscription.companyId}
			/>
		</Fragment>
	) : null;
};

export default AssignSeatSection;
